import { Component } from '@theme/component';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Plays the cart's free-shipping-unlocked "gift settles into place" entrance
 * animation every time the cart drawer opens (explicit request - was
 * previously one-time per session via sessionStorage, not anymore: closing
 * and reopening the drawer replays it every time).
 *
 * Mirrors cart-drawer.js's own DrawerOpenEvent handling (including its
 * "drawer already open before this module loaded" defensive check) rather
 * than relying on connectedCallback alone: this component's markup sits
 * inside the drawer's <dialog> from initial render (whenever the cart is
 * non-empty), so connectedCallback can fire long before the visitor
 * actually opens the drawer - animating then would finish invisibly,
 * dialog still closed, before they ever see it.
 *
 * @extends {Component}
 */
export class CartShippingUnlockedComponent extends Component {
  static #animateClass = 'cart-shipping-unlocked--animate';

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  connectedCallback() {
    super.connectedCallback();
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // Same defensive check as cart-drawer.js: a restored page can have
    // [open] already set before this module finishes loading, meaning the
    // real theme-drawer:open event already fired and this listener missed
    // it entirely.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  #handleDrawerOpen = () => {
    // Rejoue a CHAQUE ouverture, y compris si la classe est deja presente
    // d'une ouverture precedente (sinon re-ajouter une classe deja presente
    // ne fait rien - aucune animation CSS ne redemarre). On la retire, on
    // force un reflow pour que le navigateur "oublie" l'etat de
    // l'animation precedente, puis on la rajoute.
    this.classList.remove(CartShippingUnlockedComponent.#animateClass);
    void this.offsetWidth;
    this.classList.add(CartShippingUnlockedComponent.#animateClass);
  };
}

if (!customElements.get('cart-shipping-unlocked-component')) {
  customElements.define('cart-shipping-unlocked-component', CartShippingUnlockedComponent);
}
